/** DOM gesture contracts used by the application. Moveable's rotate outputs are separate. */
export const gestureEvents = new Set([
  'tap',
  'press',
  'pressup',
  'pan',
  'panstart',
  'panmove',
  'panend',
  'pancancel',
  'swipe',
  'swipeleft',
  'swiperight',
  'pinch',
  'pinchstart',
  'pinchmove',
  'pinchend',
  'pinchcancel',
]);

export interface GestureInput {
  readonly type: string;
  readonly target: EventTarget | null;
  readonly srcEvent: PointerEvent;
  readonly deltaX: number;
  readonly deltaY: number;
  /** Legacy direction values retained for the two existing drag consumers. */
  readonly direction: 1 | 2 | 4 | 8 | 16;
  readonly scale: number;
  readonly pointerType: string;
  preventDefault(): void;
}

export interface GestureScheduler {
  schedule(callback: () => void, delay: number): () => void;
}

const defaultScheduler: GestureScheduler = {
  schedule(callback, delay) {
    const timer = setTimeout(callback, delay);
    return () => clearTimeout(timer);
  },
};

interface Point {
  readonly x: number;
  readonly y: number;
}
const point = (event: PointerEvent): Point => ({
  x: event.clientX,
  y: event.clientY,
});
const distance = (a: Point, b: Point): number =>
  Math.hypot(a.x - b.x, a.y - b.y);

/** Recognizes only application gestures; elapsed time comes from monotonic DOM timestamps. */
export class PointerGestureRecognizer {
  private readonly pointers = new Map<number, Point>();
  private start: PointerEvent | undefined;
  private last: PointerEvent | undefined;
  private cancelPress: (() => void) | undefined;
  private maxDistance = 0;
  private multiplePointers = false;
  private pinchBlocked = false;
  private panning = false;
  private pinching = false;
  private pressed = false;
  private pinchDistance = 0;
  private scale = 1;
  private destroyed = false;

  constructor(
    private readonly enabled: (family: string) => boolean,
    private readonly emit: (event: GestureInput) => void,
    private readonly scheduler: GestureScheduler = defaultScheduler,
  ) {}

  get active(): boolean {
    return this.pointers.size > 0;
  }

  down(event: PointerEvent): void {
    if (
      this.destroyed ||
      event.button !== 0 ||
      this.pointers.has(event.pointerId)
    )
      return;
    if (this.start && this.start.pointerType !== event.pointerType) return;
    if (!this.active) {
      this.start = event;
      this.last = event;
      this.maxDistance = 0;
      this.multiplePointers = false;
      this.pinchBlocked = false;
      this.pressed = false;
      this.scale = 1;
    }
    this.pointers.set(event.pointerId, point(event));
    if (this.pointers.size === 1) {
      if (this.enabled('press')) {
        this.cancelPress = this.scheduler.schedule(() => {
          this.cancelPress = undefined;
          if (
            !this.destroyed &&
            this.active &&
            !this.multiplePointers &&
            this.maxDistance < 9
          ) {
            this.pressed = true;
            this.dispatch('press', this.last!);
          }
        }, 251);
      }
      return;
    }
    this.multiplePointers = true;
    this.stopPressTimer();
    if (this.panning) {
      this.panning = false;
      this.dispatch('pancancel', this.last!);
    }
    if (this.pointers.size === 2)
      this.pinchDistance = this.currentPinchDistance();
    // Extra fingers cancel the current pinch rather than changing its coordinate system.
    if (this.pointers.size > 2) {
      this.pinchBlocked = true;
      if (this.pinching) {
        this.pinching = false;
        this.dispatch('pinchcancel', event);
      }
    }
  }

  move(event: PointerEvent): void {
    if (!this.pointers.has(event.pointerId) || this.destroyed) return;
    this.pointers.set(event.pointerId, point(event));
    this.last = event;
    if (this.multiplePointers) {
      if (
        this.pointers.size !== 2 ||
        this.pinchBlocked ||
        !this.enabled('pinch')
      )
        return;
      const currentDistance = this.currentPinchDistance();
      if (this.pinchDistance === 0) {
        this.pinchDistance = currentDistance;
        return;
      }
      this.scale = currentDistance / this.pinchDistance;
      if (!this.pinching && Math.abs(this.scale - 1) < 0.001) return;
      const phase = this.pinching ? 'pinchmove' : 'pinchstart';
      this.pinching = true;
      this.dispatch(phase, event);
      this.dispatch('pinch', event);
      return;
    }
    this.maxDistance = Math.max(
      this.maxDistance,
      distance(point(this.start!), point(event)),
    );
    if (this.maxDistance >= 9) this.stopPressTimer();
    const deltaX = event.clientX - this.start!.clientX;
    const deltaY = event.clientY - this.start!.clientY;
    if (!this.enabled('pan')) return;
    if (
      !this.panning &&
      (Math.abs(deltaX) <= 10 || Math.abs(deltaX) <= Math.abs(deltaY))
    )
      return;
    const phase = this.panning ? 'panmove' : 'panstart';
    this.panning = true;
    this.dispatch(phase, event);
    this.dispatch('pan', event);
  }

  up(event: PointerEvent): void {
    if (!this.pointers.has(event.pointerId) || this.destroyed) return;
    this.pointers.set(event.pointerId, point(event));
    this.last = event;
    this.stopPressTimer();
    if (this.multiplePointers) {
      if (this.pinching) {
        this.pinching = false;
        this.dispatch('pinchend', event);
      }
    } else {
      const deltaX = event.clientX - this.start!.clientX;
      const deltaY = event.clientY - this.start!.clientY;
      const duration = Math.max(0, event.timeStamp - this.start!.timeStamp);
      this.maxDistance = Math.max(this.maxDistance, Math.hypot(deltaX, deltaY));
      if (this.panning) {
        this.panning = false;
        this.dispatch('panend', event);
      }
      if (
        this.enabled('swipe') &&
        Math.abs(deltaX) > 10 &&
        Math.abs(deltaX) > Math.abs(deltaY) &&
        Math.abs(deltaX) / Math.max(1, duration) > 0.3
      ) {
        this.dispatch(deltaX < 0 ? 'swipeleft' : 'swiperight', event);
        this.dispatch('swipe', event);
      }
      if (this.pressed && this.maxDistance < 9) this.dispatch('pressup', event);
      if (!this.pressed && duration < 250 && this.maxDistance < 9)
        this.dispatch('tap', event);
    }
    this.pointers.delete(event.pointerId);
    if (!this.active) this.reset();
  }

  cancel(event: PointerEvent | undefined = this.last): void {
    if (!this.active || !event) return;
    const wasPanning = this.panning;
    const wasPinching = this.pinching;
    this.panning = false;
    this.pinching = false;
    this.stopPressTimer();
    if (wasPanning) this.dispatch('pancancel', event);
    if (wasPinching) this.dispatch('pinchcancel', event);
    this.reset();
  }

  hasPointer(pointerId: number): boolean {
    return this.pointers.has(pointerId);
  }

  destroy(): void {
    this.destroyed = true;
    this.reset();
  }

  private currentPinchDistance(): number {
    const [first, second] = this.pointers.values();
    return distance(first, second);
  }

  private stopPressTimer(): void {
    this.cancelPress?.();
    this.cancelPress = undefined;
  }

  private reset(): void {
    this.stopPressTimer();
    this.pointers.clear();
    this.panning = false;
    this.pinching = false;
    this.start = undefined;
    this.last = undefined;
  }

  private dispatch(type: string, event: PointerEvent): void {
    if (this.destroyed || !this.start) return;
    const deltaX = event.clientX - this.start.clientX;
    const deltaY = event.clientY - this.start.clientY;
    const direction =
      deltaX === 0 && deltaY === 0
        ? 1
        : Math.abs(deltaX) >= Math.abs(deltaY)
          ? deltaX < 0
            ? 2
            : 4
          : deltaY < 0
            ? 8
            : 16;
    this.emit({
      type,
      target: this.start.target,
      srcEvent: event,
      deltaX,
      deltaY,
      direction,
      scale: this.scale,
      pointerType: event.pointerType,
      preventDefault: () => event.preventDefault(),
    });
  }
}
